import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import React, { useEffect, useState } from 'react'
import { FaStream } from 'react-icons/fa'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from './'
import GoogleSignInButton from './GoogleSignInButton'

/**
 * Header component
 * @param {Object} props - Component props
 * @param {string} props.title - Header title
 * @param {string} props.subtitle - Header subtitle
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onShowDemo - Function to show the demo walkthrough
 */
const Header = ({ title, subtitle, className = '', onShowDemo, ...rest }) => {
  const { user, signOut } = useAuth()
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const location = useLocation()

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024)
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <header
      className={`py-4 bg-white border-b border-gray-200 ${className}`}
      {...rest}
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 flex justify-between items-center">
        <div className="flex items-center">
          <Link
            to="/"
            className="text-2xl sm:text-3xl font-bold text-green-600"
            aria-label="Forkast - Homepage"
          >
            <h1 className="inline">{title}</h1>
          </Link>
          {subtitle && (
            <p className="ml-2 text-sm text-gray-500 hidden sm:block">
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-3">
          {user ? (
            <div className="flex-col flex items-center gap-1 lg:gap-3">
              <div className="flex items-center gap-2 lg:gap-4">
                <div className="flex items-center gap-1">
                  {user.picture && (
                    <Link to="/profile" aria-label="View your profile">
                      <img
                        src={user.picture}
                        alt={`Profile photo of ${user.name || 'User'}`}
                        className="w-5 h-5 lg:w-7 lg:h-7 rounded-full border border-gray-200 cursor-pointer"
                      />
                    </Link>
                  )}
                  <div className="text-xs lg:text-sm text-gray-600">
                    <Link
                      to="/profile"
                      className="font-medium hover:text-green-600"
                      aria-label="View your profile"
                    >
                      {user.name || user.email}
                    </Link>
                  </div>
                </div>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={signOut}
                  className="text-xs px-1 py-0 lg:text-sm lg:px-3 lg:py-1.5"
                  aria-label="Sign out of your account"
                >
                  Sign Out
                </Button>
              </div>
              <nav
                className="flex justify-center items-center gap-3"
                aria-label="User navigation"
              >
                {/* Activity Feed button - only show on non-mobile */}
                <Link
                  to="/activity"
                  className="hidden md:flex text-sm text-gray-600 hover:text-green-600 items-center gap-1"
                  aria-label="View activity feed"
                >
                  <FaStream className="h-4 w-4" aria-hidden="true" />
                  Activity
                </Link>
                <Link
                  to="/calendar-connections"
                  className="text-sm text-gray-600 hover:text-green-600 flex items-center gap-1"
                  aria-label="Manage calendar connections"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Connections
                </Link>
                {onShowDemo && (
                  <button
                    onClick={onShowDemo}
                    className="text-sm text-gray-600 hover:text-green-600 flex items-center gap-1"
                    title="Show App Tutorial"
                    aria-label="Show app tutorial"
                  >
                    <QuestionMarkCircleIcon
                      className="h-4 w-4"
                      aria-hidden="true"
                    />
                    <span className="hidden sm:inline">Help</span>
                  </button>
                )}
              </nav>
            </div>
          ) : (
            isDesktop && (
              <div className="h-10 flex items-center">
                <GoogleSignInButton />
              </div>
            )
          )}
        </div>
      </div>
    </header>
  )
}

export default Header
